import { useState } from 'react';
import { Stepper } from 'components/Stepper';
import { DashboardScreen, trackEvent } from 'helpers/mixpanelEvents';
import { ProfileProgressModal } from './ProfileProgressModal';
import { useMediaQuery } from 'hooks/useMediaQuery';

const SCREEN: DashboardScreen = 'recipients_dashboard';

const STEP_LABELS = ['Stripe Onboarding Complete', 'Account Verified', 'First Donation Received'];

interface ProfileProgressProps {
  /** onboarding_form_complete === true AND onboarding_status === COMPLETE */
  stepOneComplete: boolean;
  /** is_stripe_authorized === true */
  stepTwoComplete: boolean;
  /** Recipient has received at least one Stripe donation. */
  stepThreeComplete: boolean;
}

export const ProfileProgress = ({
  stepOneComplete,
  stepTwoComplete,
  stepThreeComplete,
}: ProfileProgressProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const steps = [stepOneComplete, stepTwoComplete, stepThreeComplete];
  const completedCount = steps.filter(Boolean).length;
  const isComplete = completedCount === STEP_LABELS.length;
  const firstIncomplete = steps.findIndex(s => !s);
  const activeStep = firstIncomplete === -1 ? STEP_LABELS.length + 1 : firstIncomplete + 1;

  const openModal = () => {
    trackEvent('Profile Progress Modal Opened', { screen: SCREEN });
    setIsModalOpen(true);
  };

  const handleClick = () => {
    if (isMobile) {
      openModal();
    }
  };

  return (
    <>
      <div
        className="profile-progress"
        onClick={handleClick}
        role={isMobile ? 'button' : undefined}
        tabIndex={isMobile ? 0 : undefined}
        onKeyDown={isMobile ? e => e.key === 'Enter' && openModal() : undefined}
      >
        <div className="profile-progress__label">
          <span className="profile-progress__title">
            {isComplete ? 'Your Profile is complete!' : 'Profile almost complete'}
          </span>
          <span className="profile-progress__subtitle">
            {completedCount}/{STEP_LABELS.length} steps complete:
          </span>
        </div>
        <div className="profile-progress__stepper">
          <Stepper steps={STEP_LABELS} activeStep={activeStep} />
        </div>
      </div>

      <ProfileProgressModal
        isOpen={isModalOpen}
        onClose={() => {
          trackEvent('Profile Progress Modal Closed', { screen: SCREEN });
          setIsModalOpen(false);
        }}
        stepOneComplete={stepOneComplete}
        stepTwoComplete={stepTwoComplete}
        stepThreeComplete={stepThreeComplete}
      />
    </>
  );
};
